import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';

import { CompanyProjectItemComponent } from '@app/components/company/company-projects/company-project-item/company-project-item.component';

import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { NotificationsService } from '@app/shared/services/notifications.service';
import { ProjectsDataService } from '@app/shared/services/projects-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { Company } from '@app/shared/types/models/company/company.model';
import { Project } from '@app/shared/types/models/project/project.model';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  imports: [CompanyProjectItemComponent, TranslatePipe, UiSpinnerDirective],
  selector: 'proffeo-company-draft-projects-view',
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './company-draft-projects-view.component.html'
})
export class CompanyDraftProjectsViewComponent {
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly projectService: ProjectsDataService = inject(ProjectsDataService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly notificationService: NotificationsService = inject(NotificationsService);

  protected readonly companyDraftProjects: Signal<Project[]> = computed(() =>
    this.projectService.getCompanyDraftProjects(this.activeCompany()?.id)()
  );
  protected activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;

  public constructor() {
    if (this.activeCompany()) {
      this.projectService.loadCompanyDraftProjects(this.activeCompany()!.id);
    }
  }

  protected openProjectTagDeleteConfirmationDialog(project: Project): void {
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('company-projects.modals.delete-title'),
        desc: this.translateService.instant('company-projects.modals.delete-description', {
          name: project.name
        })
      })
      .afterClosed()
      .subscribe(decision => {
        if (decision) {
          this.deleteProject(project);
        }
      });
  }

  protected deleteProject(project: Project): void {
    this.projectService.deleteProject(project, this.activeCompany()?.id, true);
  }

  protected openEditCompanyProjectDialog(project: Project): void {
    this.dialogService.openEditCompanyProjectDialog(project, true);
  }
}
